import React, { useState } from "react";
import { ArrowRight, Check, AlertTriangle, XCircle, HelpCircle, Bot } from "lucide-react";

function aeoColor(score) {
  if (score >= 80) return "#1DB954";
  if (score >= 60) return "#B45309";
  return "#FF331F";
}

const STATUS_ICON = {
  pass: <Check size={14} className="text-[#1DB954]" />,
  warn: <AlertTriangle size={14} className="text-[#B45309]" />,
  fail: <XCircle size={14} className="text-[#FF331F]" />,
  unknown: <HelpCircle size={14} className="text-[#888]" />,
};

export const AeoPanel = ({ aeo, isLocked, onUnlock }) => {
  const [expanded, setExpanded] = useState(false);
  if (!aeo || typeof aeo.score !== "number") return null;

  if (isLocked) {
    return (
      <section className="border-b-2 border-[#111]" data-testid="aeo-locked-teaser">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 text-center">
          <div className="font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#FF331F] mb-2">
            🔒 AEO Readiness locked
          </div>
          <h2 className="font-serif-display text-3xl md:text-4xl font-black leading-tight">
            How visible is this site to ChatGPT, Perplexity &amp; Google AI?
          </h2>
          <p className="mt-3 text-sm text-[#333] max-w-xl mx-auto">
            We ran {aeo.checks?.length || 12} technical Answer Engine Optimization checks on this
            URL. Sign up to see the score, what passed, and exactly what to fix.
          </p>
          <button
            onClick={onUnlock}
            className="mt-6 inline-flex items-center gap-2 bg-[#111] text-[#F4F4F0] px-6 py-3 font-mono-plex text-xs uppercase tracking-[0.25em] hover:bg-[#FF331F] transition-colors"
            data-testid="unlock-aeo-cta"
          >
            🔓 Sign up to unlock <ArrowRight size={14} />
          </button>
        </div>
      </section>
    );
  }

  const shown = expanded ? aeo.checks : aeo.checks.slice(0, 6);

  return (
    <section className="border-b-2 border-[#111]" data-testid="aeo-panel">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="flex items-center gap-2 mb-3 font-mono-plex text-[10px] uppercase tracking-[0.3em] text-[#555]">
          <Bot size={12} className="text-[#FF331F]" /> Answer Engine Optimization
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold tracking-tight leading-none">
              AEO Readiness
            </h2>
            <p className="mt-4 text-sm text-[#333] leading-relaxed">
              How visible this site is to AI answer engines — ChatGPT, Perplexity, Google AI
              Overviews. Based on {aeo.checks.length} technical checks run directly against the
              page, its robots.txt, and llms.txt.
            </p>
            <div className="mt-6 border-2 border-[#111] bg-white p-6 inline-block">
              <div className="flex items-end gap-3">
                <span className="score-display text-6xl" style={{ color: aeoColor(aeo.score) }} data-testid="aeo-score">
                  {aeo.score}
                </span>
                <span className="font-mono-plex text-[10px] uppercase tracking-[0.25em] text-[#555] pb-2">/100</span>
              </div>
              <div className="mt-1 font-mono-plex text-xs uppercase tracking-[0.25em]" data-testid="aeo-grade">
                {aeo.grade}
              </div>
            </div>
            <p className="mt-4 text-[11px] text-[#777] leading-relaxed" data-testid="aeo-method-note">
              Deterministic technical checks — computed by code, separate from the Blueprint
              analysis. {aeo.page_fetched ? "" : "The page itself could not be fetched, so only robots/llms.txt checks ran."}
            </p>
          </div>

          <div className="lg:col-span-8">
            <div className="border-2 border-[#111] bg-white divide-y-2 divide-[#111]">
              {shown.map((c) => (
                <div key={c.id} className="p-4 flex items-start gap-3" data-testid={`aeo-check-${c.id}`}>
                  <span className="mt-0.5 shrink-0">{STATUS_ICON[c.status] || STATUS_ICON.unknown}</span>
                  <div className="min-w-0">
                    <div className="font-mono-plex text-xs uppercase tracking-widest font-semibold">
                      {c.label}
                    </div>
                    <div className="mt-1 text-sm text-[#333]">{c.detail}</div>
                    {c.recommendation && (
                      <div className="mt-1 text-sm text-[#555]">
                        <span className="font-mono-plex text-[10px] uppercase tracking-widest text-[#FF331F]">Fix: </span>
                        {c.recommendation}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
            {aeo.checks.length > 6 && (
              <button
                onClick={() => setExpanded((v) => !v)}
                className="mt-4 font-mono-plex text-xs uppercase tracking-[0.25em] border-2 border-[#111] px-4 py-2 hover:bg-[#111] hover:text-[#F4F4F0] transition-colors"
                data-testid="aeo-toggle-checks"
              >
                {expanded ? "Show fewer checks" : `Show all ${aeo.checks.length} checks`}
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AeoPanel;
